import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { PieChartCard } from "@/components/charts/PieChartCard";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFilteredGA4 } from "@/hooks/useFilteredData";
import { fillDateRange } from "@/lib/chart-utils";
import { GeoOverview, DemoOverview } from "@/components/charts/GeoTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Globe, Search, Share2, Mail } from "lucide-react";
import { LoadingState } from '@/components/ui/spinner';

export default function Ga4TrafficPage() {
  const { data, accounts, filters, setFilters, loading } = useFilteredGA4();

  const sourceMedium = useMemo(() => {
    const map = new Map<string, { sessions: number; users: number; bounceRate: number; count: number }>();
    data.forEach((d) => {
      const key = `${d.source} / ${d.medium}`;
      const curr = map.get(key) || { sessions: 0, users: 0, bounceRate: 0, count: 0 };
      curr.sessions += d.sessions; curr.users += d.totalusers; curr.bounceRate += d.bounce_rate; curr.count++;
      map.set(key, curr);
    });
    return Array.from(map.entries())
      .map(([name, { sessions, users, bounceRate, count }]) => ({ name, sessions, users, bounceRate: bounceRate / count }))
      .sort((a, b) => b.sessions - a.sessions);
  }, [data]);

  const channelTrend = useMemo(() => {
    const map = new Map<string, { organic: number; direct: number }>();
    data.forEach((d) => {
      const curr = map.get(d.date) || { organic: 0, direct: 0 };
      if (d.default_channel_group === "Organic Search") curr.organic += d.sessions;
      else if (d.default_channel_group === "Direct") curr.direct += d.sessions;
      map.set(d.date, curr);
    });
    const raw = Array.from(map.entries()).map(([date, { organic, direct }]) => ({ date, value: organic, value2: direct }));
    return fillDateRange(raw, filters.dateFrom, filters.dateTo);
  }, [data, filters.dateFrom, filters.dateTo]);

  const deviceData = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.devicecategory, (map.get(d.devicecategory) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [data]);

  const channelPie = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.default_channel_group, (map.get(d.default_channel_group) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [data]);

  const byChannel = (ch: string) => data.filter((d) => d.default_channel_group === ch).reduce((s, d) => s + d.sessions, 0);

  if (loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Zdroje návštěvnosti" description="Odkud přicházejí návštěvníci" />

      <FilterBar filters={filters} onChange={setFilters} accounts={accounts} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Organic Search" value={byChannel("Organic Search")} icon={<Search className="h-4 w-4 text-primary" />} />
        <MetricCard label="Direct" value={byChannel("Direct")} icon={<Globe className="h-4 w-4 text-primary" />} />
        <MetricCard label="Organic Social" value={byChannel("Organic Social")} icon={<Share2 className="h-4 w-4 text-primary" />} />
        <MetricCard label="Email" value={byChannel("Email")} icon={<Mail className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Organic vs Direct" data={channelTrend} dataKey="value" dataKey2="value2" color="#7c9a5e" color2="#b07cc9" label1="Organic" label2="Direct" />
        <PieChartCard title="Rozložení dle kanálu" data={channelPie} />
      </div>

      <PieChartCard title="Zařízení" data={deviceData} />

      <GeoOverview data={data} />

      <DemoOverview data={data} />

      <Card>
        <CardHeader><CardTitle className="text-base">Source / Medium detail</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source / Medium</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                <TableHead className="text-right">Uživatelé</TableHead>
                <TableHead className="text-right">Bounce Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sourceMedium.slice(0, 15).map((row) => (
                <TableRow key={row.name}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-right">{row.sessions.toLocaleString("cs-CZ")}</TableCell>
                  <TableCell className="text-right">{row.users.toLocaleString("cs-CZ")}</TableCell>
                  <TableCell className="text-right">{(row.bounceRate * 100).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
